// "Progress": today's lists and "where is this part?": ONE copy of the reads and rules, used by the
// desktop Progress page (pages/Progress.js) and the phone (mobile/screens/ProgressScreen.jsx). Read-only.
import config from "../../config.js";

const API_BASE = config.API_BASE_URL;

export function formatIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Helper: Central‑time date formatter  (MM/DD/YYYY)
export function formatDateCentral(isoString) {
  if (!isoString) return "";

  // If it’s just YYYY‑MM‑DD, shift to 12:00 UTC so TZ math can’t cross a day
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(isoString);
  const utcString = isDateOnly ? `${isoString}T12:00:00Z` : isoString;

  const dateObj = new Date(utcString);
  return dateObj.toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
  });
}

/**
 * NEW helper: Return date+time in MM/DD/YY HH:MM (24-hour) format for start/end columns
 */
export function formatDateTimeCentralShort(isoString) {
  if (!isoString) return "";
  const dateObj = new Date(isoString);

  // Date in MM/DD/YY
  const dateStr = dateObj.toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

  // Time in HH:MM (24-hour)
  const timeStr = dateObj.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Chicago",
    hour12: false,
  });

  return `${dateStr} ${timeStr}`;
}

/**
 * True when a job should drop off the "open jobs" list — either it is
 * finished (Completed / Done / Shipped) OR it has been killed (Voided /
 * Cancelled).
 *
 * Every job has a Shipping task, so we look at that row: a fully-defective
 * auto-void flips Shipping to 'Voided' (while Laser/Fabrication stay
 * 'Completed'), and a manual void_job marks every non-terminal task — incl.
 * Shipping — 'Voided'. Either way the order is dead and must be hidden, just
 * like a shipped/completed one. Mirrors the jobq-api convention that
 * status IN ('Cancelled','Voided') is a dead job/task everywhere.
 */
export function isJobClosed(job) {
  const done = /(completed|complete|done|shipped|voided|cancell?ed)/i;

  // 0) root‑level flag
  const rootStatus = (job.job_status || job.status || "").trim();
  if (done.test(rootStatus)) {
    return true;
  }

  // 1) raw tasks array
  const taskShip = (job.tasks || []).find((t) =>
    /ship/i.test(t.workstation || "")
  );
  if (taskShip && done.test((taskShip.status || "").trim())) {
    return true;
  }

  // 2) merged stations array
  const stationShip = (job.stations || []).find((s) =>
    /ship/i.test(s.station || "")
  );
  if (stationShip && done.test((stationShip.status || "").trim())) {
    return true;
  }

  return false;
}

// Helper to parse a date string into a timestamp. Returns 0 if invalid.
export function parseDate(str) {
  if (!str) return 0;
  return new Date(str).getTime();
}

/**
 * Derive an "estimated completion date" from the final production station
 * (excluding Shipping). We'll define a station rank and pick the station
 * with the largest rank, then return its scheduled_date.
 */
export function getEstimatedCompletionDateFromStations(stations = []) {
  // Excluding "Shipping" from the rank, so "Finish" is effectively last
  const stationRank = {
    Laser: 1,
    Tap: 2,
    Brake: 3,
    Fabrication: 4,
    Blast: 5,
    Finish: 6,
    // "Shipping" intentionally not included
  };

  // Filter only those in the "production" list
  const productionStations = stations.filter(
    (s) => stationRank[s.station] != null
  );

  // Among production stations, find the one with the highest rank
  let finalStation = null;
  let highestRank = 0;
  for (const st of productionStations) {
    const rank = stationRank[st.station] || 0;
    if (rank > highestRank) {
      highestRank = rank;
      finalStation = st;
    }
  }

  if (!finalStation) {
    return null; // No production station found
  }
  return finalStation.scheduled_date || null;
}

/**
 * Canonical shop-floor station order. A job advances left→right through these;
 * "Shipping" is the terminal station.
 */
/** A station row counts as done (the progress bar's rule). */
export function isStationDone(status) {
  return /complete|done|shipped/i.test(status || "");
}

export const STATION_FLOW = [
  "Laser",
  "Tap",
  "Brake",
  "Fabrication",
  "Blast",
  "Finish",
  "Shipping",
];

/**
 * Measure how far a job has advanced through its production stations.
 *
 * Mirrors the progress logic in <PartProgressDisplay/> so the job list can be
 * ordered by real shop-floor progress (furthest-along first), not just by the
 * derived estimated completion date. Returns:
 *   - stationsPresent: the ordered stations this job actually has
 *   - currentIndex:    number of leading stations already complete (i.e. the
 *                      index of the first not-yet-complete station; equals
 *                      stationsPresent.length when every station is complete)
 *   - total:           count of stations present
 *   - fraction:        currentIndex / total  (0 = not started, 1 = all done)
 */
export function getProductionProgress(stations = []) {
  const list = stations || [];
  const stationsPresent = STATION_FLOW.filter((st) =>
    list.some((x) => x.station === st)
  );

  let currentIndex = stationsPresent.findIndex((st) => {
    const stationObj = list.find((x) => x.station === st);
    return stationObj && !isStationDone(stationObj.status);
  });
  if (currentIndex === -1) {
    currentIndex = stationsPresent.length; // every station complete
  }

  const total = stationsPresent.length;
  const fraction = total > 0 ? currentIndex / total : 0;
  return { stationsPresent, currentIndex, total, fraction };
}

// ── Reads ──

/**
 * Today's three lists: finishing (GET /progress/completing_today), shipped and ready to ship
 * (GET /queue/shipping_details, today as the LOCAL date). A list that answers with an error is [];
 * a request that never answers throws (the page shows the error).
 */
export async function loadTodayLists(now = new Date()) {
  const failed = [];
  const finRes = await fetch(`${API_BASE}/progress/completing_today`);
  let finishing = [];
  if (finRes.ok) {
    finishing = await finRes.json();
  } else {
    failed.push("finishing");
  }
  const todayStr = formatIsoDate(now);
  const shipRes = await fetch(
    `${API_BASE}/queue/shipping_details?shipped=true&start_date=${todayStr}&end_date=${todayStr}`
  );
  let shipped = [];
  if (shipRes.ok) {
    shipped = await shipRes.json();
  } else {
    failed.push("shipped");
  }
  const readyRes = await fetch(
    `${API_BASE}/queue/shipping_details?shipped=false&start_date=${todayStr}&end_date=${todayStr}`
  );
  let ready = [];
  if (readyRes.ok) {
    ready = await readyRes.json();
  } else {
    failed.push("ready");
  }
  // `failed`: the lists that answered with an error (the phone says so instead of "none today").
  return { finishing, shipped, ready, failed };
}

/** GET /parts/search?query=: the parts matching what was typed ([] for an empty query or a failure). */
export async function searchParts(query) {
  if (!String(query || "").trim()) return [];
  try {
    const res = await fetch(`${API_BASE}/parts/search?query=${encodeURIComponent(query)}`);
    if (!res.ok) {
      console.warn("Failed to search for:", query);
      return [];
    }
    return await res.json();
  } catch (err) {
    console.error("Search error:", err);
    return [];
  }
}

const STATION_RANK = Object.freeze({
  Laser: 1,
  Tap: 2,
  Brake: 3,
  Fabrication: 4,
  Blast: 5,
  Finish: 6,
  Shipping: 7, // always included in the JSON for each part
});

// One job of the part: read its tasks and keep the stations THIS part needs (its own tasks plus the
// sheet's Laser row, part_id null), one row per station in shop order, then its estimated completion.
async function withStations(job) {
  if (!job.job_id) return job;
  try {
    const jobProgRes = await fetch(`${API_BASE}/jobs/${job.job_id}/progress`);
    if (!jobProgRes.ok) {
      console.warn("Could not fetch job progress for job:", job.job_id);
      return job;
    }
    const jobProgData = await jobProgRes.json();
    // The job-level status (Voided / Cancelled / Completed / ...) so a fully-voided job is filtered
    // even before its Shipping station is read.
    job.job_status = jobProgData.job_status ?? job.job_status ?? job.status;
    const allTasks = jobProgData.tasks || [];
    const relevantTasks = allTasks.filter(
      (t) =>
        t.part_id === job.part_id ||
        (t.part_id == null && t.workstation === "Laser")
    );
    const stationsNeeded = [...new Set(relevantTasks.map((t) => t.workstation))];
    job.stations = stationsNeeded.map((st) => {
      const taskRow = relevantTasks.find((t) => t.workstation === st) || {};
      return {
        station: st,
        status: taskRow.status || "",
        scheduled_date: taskRow.scheduled_date || null,
        start_time: taskRow.start_time || null,
        end_time: taskRow.end_time || null,
      };
    });
    job.stations.sort((a, b) => (STATION_RANK[a.station] || 999) - (STATION_RANK[b.station] || 999));
    job.estimated_completion_date = getEstimatedCompletionDateFromStations(job.stations);
    return job;
  } catch (err) {
    console.warn("Error merging stations for job:", job.job_id, err);
    return job;
  }
}

/**
 * "Where is this part?": its open jobs (GET /progress/part/{id}; shipped / completed / voided /
 * cancelled dropped before and after each job's own read, 3 job reads at a time), each with its
 * stations, the furthest along first, then the earliest estimated completion. Throws when the part
 * has no progress record.
 */
export async function loadPartProgress(partId) {
  const res = await fetch(`${API_BASE}/progress/part/${partId}`);
  if (!res.ok) {
    // 400 = the part has no active jobs (the phone reads that as "none open"; anything else failed).
    const err = new Error(`Part progress not found for part_id=${partId}`);
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  let jobsArray = Array.isArray(data) ? data : [data];
  jobsArray = jobsArray.filter((job) => !isJobClosed(job));

  const concurrencyLimit = 3;
  const queue = [...jobsArray];
  const results = [];
  async function runQueue() {
    const batch = queue.splice(0, concurrencyLimit);
    await Promise.all(batch.map((job) => withStations(job).then((updated) => { results.push(updated); })));
    if (queue.length > 0) return runQueue();
    return undefined;
  }
  await runQueue();

  const visibleJobs = results.filter((job) => !isJobClosed(job));
  // Furthest along in production first; within the same progress, the earliest estimated completion
  // (no date last, so an unscheduled job can't leap to the top).
  visibleJobs.sort((a, b) => {
    const fracA = getProductionProgress(a.stations).fraction;
    const fracB = getProductionProgress(b.stations).fraction;
    if (fracB !== fracA) return fracB - fracA;
    const dateA = parseDate(a.estimated_completion_date) || Infinity;
    const dateB = parseDate(b.estimated_completion_date) || Infinity;
    if (dateA !== dateB) return dateA - dateB;
    return (a.job_id || 0) - (b.job_id || 0); // the job reads finish in any order: keep ties stable
  });
  return visibleJobs;
}
